import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkExpertNotificationAsViewedMutation } from 'shared/hooks/api/markExpertNotificationAsViewed/markExpertNotificationAsViewed.generated';
import { useMarkManyExpertNotificationsAsViewedMutation } from 'shared/hooks/api/markManyExpertNotificationAsViewed/markManyExpertNotificationsAsViewed.generated';
import { useNotificationsQuery } from 'shared/hooks/api/notifications/notifications.generated';
import { useNotificationStore } from 'shared/hooks/stores/useNotificationStore';
import { useSitemap } from 'shared/hooks/useSitemap';
import { NotificationTypeEnum } from 'types/enums/notification-type.enum';
import { useShallow } from 'zustand/react/shallow';

export const useNotifications = () => {
  const navigate = useNavigate();
  const sitemap = useSitemap();
  const {
    notifications,
    markNotificationAsViewed,
    markManyNotificationsAsViewed,
    loadNotifications,
  } = useNotificationStore(
    useShallow((state) => ({
      notifications: state.notifications,
      markNotificationAsViewed: state.markNotificationAsViewed,
      markManyNotificationsAsViewed: state.markManyNotificationsAsViewed,
      loadNotifications: state.loadNotifications,
    })),
  );
  const [markNotificationAsViewedMutation, { loading: markAsViewedLoading }] =
    useMarkExpertNotificationAsViewedMutation();
  const [
    markManyExpertNotificationsAsViewedMutation,
    { loading: markManyExpertNotificationsAsViewedLoading },
  ] = useMarkManyExpertNotificationsAsViewedMutation();
  const { loading, refetch: refetchNotifications } = useNotificationsQuery({
    skip: !!notifications.length,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({ expertNotifications }) =>
      loadNotifications(
        expertNotifications.notifications.filter(({ type, id }) => {
          const isTypeKnown = Object.values(NotificationTypeEnum).includes(
            type as unknown as NotificationTypeEnum,
          );
          if (!isTypeKnown) {
            console.error(`Unknown notification type ${type} with id ${id}`);
          }
          return isTypeKnown;
        }),
      ),
  });

  const redirectToRelatedAndMarkAsViewed = useCallback(
    async (notificationId: string) => {
      const notification = notifications.find((notif) => notif.id === notificationId);
      if (!notification) {
        return;
      }
      if (!notification.viewedAt) {
        const res = await markNotificationAsViewedMutation({
          variables: {
            notificationId,
          },
        });
        if (res.data?.markExpertNotificationAsViewed.viewedAt) {
          markNotificationAsViewed(
            notificationId,
            res.data.markExpertNotificationAsViewed.viewedAt,
          );
        }
      }
      if (notification.jobOrderId) {
        navigate(sitemap.jobOrder(notification.jobOrderId));
      }
    },
    [sitemap, notifications],
  );

  const markAsViewedByType = useCallback(
    async (type: NotificationTypeEnum) => {
      const notificationIdsToMark = notifications
        .filter((notif) => notif.type === type && !notif.viewedAt)
        .map(({ id }) => id);
      if (!notificationIdsToMark.length) {
        return;
      }

      const res = await markManyExpertNotificationsAsViewedMutation({
        variables: {
          notificationIds: notificationIdsToMark,
        },
      });

      if (res.data?.markManyExpertNotificationsAsViewed) {
        markManyNotificationsAsViewed(res.data.markManyExpertNotificationsAsViewed);
      }
    },
    [notifications],
  );

  return {
    notifications,
    redirectToRelatedAndMarkAsViewed,
    markAsViewedByType,
    loading,
    markAsViewedLoading,
    markManyExpertNotificationsAsViewedLoading,
    refetchNotifications,
  };
};
